import { useCallback, useState, useMemo } from 'react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../entityProvider.factory';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  Pagination,
  Provider,
  ProviderFilters,
  TemplateEntity,
  TextFieldValue,
  SearchFilter,
  StringFilter,
  WbTableFilters,
  KindEnum,
  selectTemplateEntity,
} from '@agilelab/plugin-wb-platform';
import { useColumns } from '../hooks';
import { cleanParams, parseDynamicFieldsSimple } from './utils';
import { parseNunjucks } from '@agilelab/plugin-wb-platform-common';

const validPageSizes = [5, 10, 25];

interface RemoteEntity extends GenericEntityType {
  id: string;
  name: string;
  kind: KindEnum;
  [key: string]: any;
}

interface RemoteFilters extends ProviderFilters {
  filter?: string;
}

export interface RemoteContextType
  extends GenericProviderType<RemoteEntity[], RemoteFilters> {
  getAllFilteredEntities: (options?: {
    chunkSize?: number;
    onProgress?: (loadedCount: number, totalLoaded: number) => void;
  }) => Promise<RemoteEntity[]>;
}

interface FetchResult {
  items: RemoteEntity[];
  total: number;
  hasNextPage: boolean;
}

const [useRemoteContext, RemoteContextProvider] =
  contextProviderFactory<RemoteContextType>();

export default useRemoteContext;

export interface RemoteProviderProps {
  templateKind: TemplateEntity;
  children: React.ReactNode;
  formContext: any;
}

/**
 * Dynamically generates column definitions based on the actual data structure.
 * Always includes 'id' as the first column, then adds all other fields from the first item.
 * Excludes internal Backstage entity fields to avoid conflicts.
 */
const generateColumnsFromData = (data: any[]): Map<string, string> => {
  if (!data || data.length === 0) {
    return new Map<string, string>([['id', '{{id}}']]);
  }

  const firstItem = data[0];
  const columns = new Map<string, string>();

  columns.set('id', '{{id}}');

  const excludedKeys = new Set([
    'id',
    '__metadata',
    'apiVersion',
    'kind',
    'metadata',
    'spec',
    'originalId',
  ]);

  for (const key of Object.keys(firstItem)) {
    if (!excludedKeys.has(key)) {
      columns.set(key, `{{${key}}}`);
    }
  }

  return columns;
};

/**
 * Transforms any data item into a RemoteEntity format.
 * Requires the item to have an 'id' field. Generates a display label from selectedField
 * or falls back to common display fields (name, label, title, displayName).
 *
 * @param item - The raw data item from the API
 * @param selectedField - Optional field to use as the display label
 * @returns A RemoteEntity with Backstage-compatible structure
 * @throws Error if the item doesn't have an 'id' field
 */
const toRemoteEntity = (item: any, selectedField?: string): RemoteEntity => {
  if (!item.id) {
    throw new Error('Remote entity must have an id field');
  }

  const renderedField = parseNunjucks(selectedField, item);
  // Generate label from selectedField or fallback to common display fields

  const label = renderedField
    ? String(renderedField)
    : item.name ||
      item.label ||
      item.title ||
      item.displayName ||
      String(item.id);

  return {
    ...item,
    __metadata: { kind: KindEnum.remote, name: label },
  };
};

export const RemoteProvider: React.FC<RemoteProviderProps> = props => {
  const { children, templateKind, formContext } = props;
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const { apiSpec, displayField } = templateKind;

  // Use configured limit from apiSpec if available, otherwise default to 10
  // Ensure the limit is one of the valid Material-UI page size options
  const apiLimit = Number(apiSpec?.retrieval?.params?.limit) || 10;
  const configuredLimit = validPageSizes.includes(apiLimit)
    ? apiLimit
    : validPageSizes.find(size => size >= apiLimit) || 10;

  const [pagination, setPagination] = useState<Pagination>({
    limit: configuredLimit,
    currentPage: 0,
  });

  const isOldBehavior =
    config.getOptionalBoolean(
      'mesh.builder.ui.remoteProvider.oldOffsetBehaviour',
    ) ?? false;

  const [filters, setFilters] = useState<RemoteFilters>({});
  const [dynamicColumns, setDynamicColumns] = useState<Map<string, string>>(
    new Map([['id', '{{id}}']]),
  );
  const columns: Column[] = useColumns(dynamicColumns, templateKind);

  const fetchRemoteEntities = useCallback(
    async (
      currentFilters: RemoteFilters = {},
      offset?: number,
      limit?: number,
    ): Promise<FetchResult> => {
      if (!apiSpec?.retrieval)
        return { items: [], total: 0, hasNextPage: false };

      const {
        path,
        method = 'POST',
        params: defaultParams = {},
        baseUrl: rawBaseUrl,
      } = apiSpec.retrieval;

      const baseUrl =
        rawBaseUrl === '{{CURRENT_HOST}}'
          ? `${config.getOptionalString('backend.baseUrl')}/api`
          : rawBaseUrl;

      const url = `${baseUrl}${path}`;

      // Always request one extra item to check if there's a next page
      const requestLimit = (limit || 10) + 1;

      const resolvedDefaults = parseDynamicFieldsSimple(
        formContext,
        defaultParams,
      );
      const params: Record<string, any> = {
        ...resolvedDefaults,
      };

      cleanParams(params);

      try {
        const credentials = await identityApi.getCredentials();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (credentials.token && rawBaseUrl === '{{CURRENT_HOST}}') {
          headers.Authorization = `Bearer ${credentials.token}`;
        }

        const paramsObj = {
          offset: String(offset),
          limit: String(requestLimit),
          filter: currentFilters.filter || '',
        };

        cleanParams(paramsObj);

        const queryString = new URLSearchParams(paramsObj);

        const urlWithParams = queryString
          ? `${url}?${queryString.toString()}`
          : url;

        const response = await fetch(urlWithParams, {
          method,
          headers,
          body: JSON.stringify(params),
        });

        const dataJson = await response.json();
        const data = dataJson.items ?? dataJson;

        if (!Array.isArray(data))
          return { items: [], total: 0, hasNextPage: false };

        const validItems = data.filter(
          item => item && item.id !== undefined && item.id !== null,
        );

        const actualLimit = limit || 10;
        const hasNext = validItems.length > actualLimit;
        const itemsToReturn = hasNext
          ? validItems.slice(0, actualLimit)
          : validItems;

        if (itemsToReturn.length > 0) {
          setDynamicColumns(generateColumnsFromData(itemsToReturn));
        }

        const transformedItems = itemsToReturn
          .map(item => {
            try {
              return toRemoteEntity(item, displayField);
            } catch {
              return null;
            }
          })
          .filter((item): item is RemoteEntity => item !== null);

        return {
          items: transformedItems,
          total: transformedItems.length,
          hasNextPage: hasNext,
        };
      } catch {
        return { items: [], total: 0, hasNextPage: false };
      }
    },
    [apiSpec, config, identityApi, displayField, formContext],
  );

  const fetchParams = useMemo(
    () => ({
      filters,
      offset: isOldBehavior
        ? pagination.currentPage
        : pagination.currentPage * pagination.limit,
      limit: pagination.limit,
    }),
    [filters, isOldBehavior, pagination.currentPage, pagination.limit],
  );

  // Single call that handles both data and next page check
  const remoteEntitiesState = useAsync(async () => {
    const result = await fetchRemoteEntities(
      fetchParams.filters,
      fetchParams.offset,
      fetchParams.limit,
    );

    setPagination(prev => ({
      ...prev,
      countlessOptions: {
        hasNextPage: () => result.hasNextPage ?? false,
        isFirstPage: () => prev.currentPage === 0,
      },
    }));
    return result.items;
  }, [fetchParams, fetchRemoteEntities]);

  const setPaginationWithLimitReset = useCallback(
    (newPagination: Pagination | ((prev: Pagination) => Pagination)) => {
      setPagination(prev => {
        const next =
          typeof newPagination === 'function'
            ? newPagination(prev)
            : newPagination;
        if (prev.limit !== next.limit) {
          return {
            ...next,
            currentPage: 0,
          };
        }
        return next;
      });
    },
    [],
  );

  const fetchAllFilteredEntities = useCallback(
    async (
      currentFilters: RemoteFilters = {},
      chunkSize: number = 50,
      onProgress?: (loadedCount: number, totalLoaded: number) => void,
    ): Promise<RemoteEntity[]> => {
      if (!apiSpec?.retrieval) return [];

      const {
        path,
        method = 'POST',
        params: defaultParams = {},
        baseUrl: rawBaseUrl,
      } = apiSpec.retrieval;

      const baseUrl =
        rawBaseUrl === '{{CURRENT_HOST}}'
          ? `${config.getOptionalString('backend.baseUrl')}/api`
          : rawBaseUrl;

      const url = `${baseUrl}${path}`;

      const resolvedDefaults = parseDynamicFieldsSimple(
        formContext,
        defaultParams,
      );

      const allEntities: RemoteEntity[] = [];
      let offset = 0;
      let hasMoreData = true;

      try {
        const credentials = await identityApi.getCredentials();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (credentials.token) {
          headers.Authorization = `Bearer ${credentials.token}`;
        }

        while (hasMoreData) {
          const params: Record<string, any> = {
            ...resolvedDefaults,
          };

          cleanParams(params);

          const paramsObj = {
            offset: String(offset),
            limit: String(chunkSize),
            filter: currentFilters.filter || '',
          };

          cleanParams(paramsObj);

          const queryString = new URLSearchParams(paramsObj);
          const urlWithParams = queryString
            ? `${url}?${queryString.toString()}`
            : url;

          const response = await fetch(urlWithParams, {
            method,
            headers,
            body: JSON.stringify(params),
          });

          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }

          const dataJson = await response.json();
          const data = dataJson.items ?? dataJson;

          if (!Array.isArray(data) || data.length === 0) {
            hasMoreData = false;
            break;
          }

          const validItems = data.filter(
            item => item && item.id !== undefined && item.id !== null,
          );

          if (offset === 0 && validItems.length > 0) {
            setDynamicColumns(generateColumnsFromData(validItems));
          }

          const transformedItems = validItems
            .map(item => {
              try {
                return toRemoteEntity(item, displayField);
              } catch {
                return null;
              }
            })
            .filter((item): item is RemoteEntity => item !== null);

          allEntities.push(...transformedItems);

          if (onProgress) {
            onProgress(transformedItems.length, allEntities.length);
          }

          if (validItems.length < chunkSize) {
            hasMoreData = false;
          } else {
            offset += chunkSize;
          }
        }

        return allEntities;
      } catch (error) {
        throw new Error('Error fetching all filtered entities:', error);
      }
    },
    [apiSpec, config, identityApi, displayField, formContext],
  );

  const getAllFilteredEntities = useCallback(
    async (
      options: {
        chunkSize?: number;
        onProgress?: (loadedCount: number, totalLoaded: number) => void;
      } = {},
    ): Promise<any[]> => {
      const { chunkSize = 50, onProgress } = options;
      return await fetchAllFilteredEntities(filters, chunkSize, onProgress);
    },
    [fetchAllFilteredEntities, filters],
  );

  const changeFilters = useCallback(
    <K extends keyof RemoteFilters>(key: K, value: RemoteFilters[K]) => {
      setFilters(prev => ({ ...prev, [key]: value }));
      setPagination(prev => ({ ...prev, currentPage: 0 }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters({});
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  }, []);

  const contextValue: RemoteContextType = {
    columns,
    entitiesState: remoteEntitiesState,
    filters,
    changeFilters,
    resetFilters,
    pagination,
    setPagination: setPaginationWithLimitReset,
    count: -1,
    nextCursor: undefined,
    prevCursor: undefined,
    templateKind,
    getAllFilteredEntities,
  };

  return (
    <RemoteContextProvider value={contextValue}>
      {children}
    </RemoteContextProvider>
  );
};

export const RemoteFiltersSection = () => {
  const { filters, changeFilters, resetFilters, templateKind } =
    useRemoteContext();

  const filterElement = (key: string) => {
    if (key === 'search') {
      return (
        <SearchFilter
          key={key}
          filterKey="filter"
          value={filters.filter ?? ''}
          changeFilters={changeFilters}
        />
      );
    }

    return (
      <StringFilter
        key={key}
        field={key.charAt(0).toUpperCase() + key.slice(1)}
        value={filters[key] ?? undefined}
        onChange={value => changeFilters(key as keyof RemoteFilters, value)}
      />
    );
  };

  return (
    <WbTableFilters onClear={resetFilters}>
      {templateKind?.userFilters?.map(filter => filterElement(filter))}
    </WbTableFilters>
  );
};

export class RemoteHandlerProvider
  implements Provider<RemoteEntity[], RemoteFilters>
{
  private readonly templateEntity: TemplateEntity | undefined;

  constructor(availableKinds: TemplateEntity[]) {
    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.remote);
  }

  useContext = useRemoteContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: React.ReactNode,
    _: any,
    formContext: any,
  ) {
    return (
      <RemoteProvider templateKind={templateKind!} formContext={formContext}>
        {children}
      </RemoteProvider>
    );
  }

  filtersSection = RemoteFiltersSection;

  extractEntityId(entity: GenericEntityType) {
    return entity.id;
  }

  adapt(entity: GenericEntityType) {
    const remoteEntity = toRemoteEntity(entity);

    return {
      name: remoteEntity.__metadata.name,
      kind: KindEnum.remote,
      label: entity.name,
      urn: entity.id,
      // for remote entity only urn or fullObj could be returned
      // for fault tolerance urn will be returned if ref is selected on the template
      ref: entity.id,
    };
  }

  async resolve(ref: string): Promise<TextFieldValue | undefined> {
    if (!this.templateEntity?.apiSpec?.retrieval) return undefined;

    const {
      baseUrl,
      path,
      method = 'POST',
      params: defaultParams = {},
    } = this.templateEntity?.apiSpec?.retrieval;

    const url = baseUrl + path;
    const params: Record<string, any> = {
      // todo this should be parsed with parseDynamicFieldsSimple
      defaultParams,
      id: ref,
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Try to get the token from localStorage (since we don't have access to identityApi here)
    const token = localStorage.getItem('backstage:token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let response;
    if (method.toUpperCase() === 'POST') {
      response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
    } else {
      // Convert all parameter values to strings for URLSearchParams
      const stringParams: Record<string, string> = {};
      Object.entries(params).forEach(([key, value]) => {
        stringParams[key] = String(value);
      });
      const queryString = new URLSearchParams(stringParams).toString();
      response = await fetch(`${url}?${queryString}`, { headers });
    }

    const dataJson = await response.json();

    let item;
    if (Array.isArray(dataJson)) {
      item = dataJson.find(
        (e: any) =>
          e &&
          e.id !== undefined &&
          e.id !== null &&
          String(e.id) === String(ref),
      );
    } else if (dataJson.items) {
      item = dataJson.items.find(
        (e: any) =>
          e &&
          e.id !== undefined &&
          e.id !== null &&
          String(e.id) === String(ref),
      );
    } else {
      item = dataJson;
    }

    if (!item || !item.id) return undefined;

    try {
      const entity = toRemoteEntity(item, this.templateEntity?.displayField);

      return {
        kind: KindEnum.remote,
        label: entity.name,
        value: entity.originalId,
      };
    } catch (error) {
      return undefined;
    }
  }
}
